import React from 'react'

import { SortOrder } from '@/types/common'

import { cn } from '../../utils/common'
import { SortDirection } from '../../utils/constants'
import { ChevronDown, ChevronSelectorVertical, ChevronUp } from '../Icons'

import {
  Alignment,
  FreezePosition,
  getNextSortOrder,
  getStickyOffsets,
  getStickyPositionStyle,
  isFirstRightFrozenColumn,
  isLastLeftFrozenColumn,
} from './tableHelper'

export const DEFAULT_COLUMN_WIDTH = 150
const ICON_SIZE = 12

type BaseColumnProps<T> = {
  id: keyof T | string
  label?: string | React.ReactNode
  alignLabel?: Alignment
  width?: string | number
  sortable?: boolean
  sortFunction?: (a: T, b: T) => number
  align?: Alignment
  isFrozen?: boolean
  freezePosition?: FreezePosition
  cellClassName?: string | ((row: T) => string)
  disableTruncation?: boolean
  disableRowClick?: boolean
  onCellClick?: (row: T, e: React.MouseEvent<HTMLTableCellElement>) => void
}

export type DataColumn<T> = BaseColumnProps<T> & {
  id: keyof T
  renderCell?: (value: T[keyof T] | undefined, row: T) => React.ReactNode
}

export type CustomColumn<T> = BaseColumnProps<T> & {
  id: string
  renderCell?: (value: T[keyof T] | undefined, row: T) => React.ReactNode
}

export type FrozenColumn<T> = BaseColumnProps<T> & {
  renderCell?: (value: T[keyof T] | undefined, row: T) => React.ReactNode
  isFrozen: true
  freezePosition: FreezePosition
}

export type TableColumn<T> = DataColumn<T> | CustomColumn<T> | FrozenColumn<T>

export type TableHeadProps<T extends object = Record<string, unknown>> = {
  columns: TableColumn<T>[]
  orderBy: keyof T | null
  order: SortOrder
  onSort: (colId: keyof T | null, dir: SortOrder) => void
  isLoading?: boolean
}

const getSortIcon = (active: boolean, dir: SortOrder) => {
  if (!active) return <ChevronSelectorVertical size={ICON_SIZE} />
  if (dir === SortDirection.ASC) return <ChevronUp size={ICON_SIZE} />
  return <ChevronDown size={ICON_SIZE} />
}

const SortButton = ({
  active,
  dir,
  onClick,
  disabled = false,
}: {
  active: boolean
  dir: SortOrder
  onClick: () => void
  disabled?: boolean
}) => {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        if (!disabled) {
          onClick()
        }
      }}
      disabled={disabled}
      aria-pressed={active}
      className="inline-flex items-center justify-center p-0 leading-none bg-transparent border-0 cursor-pointer text-quaternary-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
    >
      {getSortIcon(active, dir)}
    </button>
  )
}

const getAlignmentClass = (align: Alignment): string => {
  if (align === Alignment.RIGHT) return 'text-right'
  if (align === Alignment.CENTER) return 'text-center'
  return 'text-left'
}

const getJustifyClass = (align: Alignment): string => {
  if (align === Alignment.RIGHT) return 'justify-end'
  if (align === Alignment.CENTER) return 'justify-center'
  return 'justify-start'
}

const getColumnStyle = (
  finalWidth: string | number | undefined,
  stickyStyle: React.CSSProperties
) => ({
  ...(finalWidth
    ? {
        width: finalWidth,
        minWidth: finalWidth,
        maxWidth: finalWidth,
      }
    : {}),
  ...stickyStyle,
})

export function TableHead<T extends object = Record<string, unknown>>({
  columns,
  orderBy,
  order,
  onSort,
  isLoading = false,
}: Readonly<TableHeadProps<T>>) {
  const theadRef = React.useRef<HTMLTableSectionElement>(null)
  const isSortingDisabled = isLoading

  const handleClick = (colId: keyof T | string) => {
    if (isSortingDisabled) return
    const isActive = orderBy === colId
    const nextOrder = getNextSortOrder(isActive, order)
    onSort?.(nextOrder.order ? (colId as keyof T) : null, nextOrder.order)
  }

  const { leftOffsets, rightOffsets } = getStickyOffsets(columns)

  return (
    <thead ref={theadRef} className="sticky top-0 z-10">
      <tr className="text-sm font-semibold text-gray-600 bg-gray-50">
        {columns.map((col, i) => {
          const alignLabel = col.alignLabel ?? Alignment.LEFT
          const isSticky = col.isFrozen
          const side = col.freezePosition ?? FreezePosition.LEFT
          const stickyStyle = getStickyPositionStyle(
            side,
            Number.parseFloat(leftOffsets[i]) || 0,
            Number.parseFloat(rightOffsets[i]) || 0
          )

          const isActive = orderBy === col.id
          const isLastLeftFrozen = isLastLeftFrozenColumn(columns, i)
          const isFirstRightFrozen = isFirstRightFrozenColumn(columns, i)

          return (
            <th
              key={String(col.id)}
              scope="col"
              className={cn(
                'px-5 py-2 whitespace-normal uppercase font-semibold text-quaternary-500 text-xs',
                getAlignmentClass(alignLabel),
                isSticky && 'sticky z-[3] bg-slate-50',
                col.sortable && !isSortingDisabled && 'cursor-pointer',
                col.sortable && isSortingDisabled && 'cursor-not-allowed'
              )}
              style={getColumnStyle(col.width, stickyStyle)}
              title={typeof col.label === 'string' ? col.label : undefined}
              onClick={
                col.sortable && !isSortingDisabled
                  ? () => handleClick(col.id)
                  : undefined
              }
            >
              {isLastLeftFrozen && (
                <div className="absolute top-0 right-0 w-px h-full pointer-events-none bg-slate-200" />
              )}
              {isFirstRightFrozen && (
                <div className="absolute top-0 left-0 w-px h-full pointer-events-none bg-slate-200" />
              )}

              <div
                className={cn(
                  'inline-flex items-center leading-none gap-1',
                  getJustifyClass(alignLabel),
                  isSticky ? 'items-start' : 'items-center'
                )}
              >
                <span
                  className={cn(
                    'inline-block p-0 leading-none',
                    isSticky && 'break-words text-center'
                  )}
                >
                  {col.label ?? ''}
                </span>
                {col.sortable && (
                  <SortButton
                    active={isActive}
                    dir={isActive ? order : SortDirection.ASC}
                    onClick={() => handleClick(col.id)}
                    disabled={isSortingDisabled}
                  />
                )}
              </div>
            </th>
          )
        })}
      </tr>
    </thead>
  )
}
